import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, FieldControl, FormControl, Loading } from "spark-core-dx/components";
import { SystemTypes } from "spark-core-dx/services";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import {
  buildClientValidationError,
  buildConnectionCheckFailureStatus,
  buildIntegrationDraft,
  buildIntegrationStepValues,
  buildResourceOperationQueryParams,
  buildServerFailureError,
  buildSetupFormMetadata,
  buildWizardValidationError,
  CONNECTION_CHECK_OPERATION,
  formatConnectionCheckStatusText,
  filterIntegrationsForSetup,
  getDisplayName,
  getFirst,
  getId,
  getIntegrationSetupFilterKey,
  getLinkedRecord,
  getLinkedRecordItemId,
  getMetadataFields,
  getOperationItem,
  getOperationItems,
  getOperationResult,
  getProductResourceDefaults,
  getValue,
  INTEGRATION_EXISTING_FORM_GROUP_ID,
  INTEGRATION_MODE_EXISTING,
  INTEGRATION_MODE_NEW,
  INTEGRATION_NEW_FORM_GROUP_ID,
  keepObjectStateWhenEqual,
  requireServiceResponse,
  requireSuccessfulServiceResponse,
  resolveEffectiveIntegrationTypeValue,
  resolveProductResourceIntegrationTypeValue,
  validateIntegrationConnectionString,
  validateRequiredIntegrationFields
} from "../resourceConfigWizardUtils";

const INTEGRATION_SETUP_HIDDEN_FIELD_NAMES = [
  "integrationtype",
  "isonline",
  "loglevels",
  "onsiteserviceuri",
  "coreapiuri",
  "isonsite"
];

const buildIntegrationQuery = (integrationTypeValue) =>
  integrationTypeValue === null || integrationTypeValue === undefined
    ? "x => x.IsActive == true && x.IsDeleted == false"
    : `x => x.IsActive == true && x.IsDeleted == false && x.IntegrationType == ${integrationTypeValue}`;

const resolvePersistedIntegrationId = (wizard) =>
  getLinkedRecordItemId(getLinkedRecord(wizard, "Integration", "SourceIntegration")) || "";

const resolveSavedIntegrationId = (wizard, values = {}) =>
  values?.IntegrationId
  || resolvePersistedIntegrationId(wizard)
  || "";

const IntegrationStepV2 = ({
  context,
  onChange,
  registerStepController
}) => {
  const { wizard, values = {}, readOnly, saving } = context;
  const resourceDomainContext = useResourceDomainContext();
  const coreState = resourceDomainContext?.coreState;
  const integrationMgr = resourceDomainContext?.managers?.integration;
  const integrationMgrReady = Boolean(integrationMgr);
  const coreStateRef = useRef(coreState);
  const integrationMgrRef = useRef(integrationMgr);
  const loadKeyRef = useRef("");
  const prepareStepValuesRef = useRef(null);
  const canContinueRef = useRef(() => false);
  const isBusyRef = useRef(false);
  const productResourceDefaults = context.productResourceDefaults || getProductResourceDefaults(wizard);
  const integrationTypeValue = resolveProductResourceIntegrationTypeValue(productResourceDefaults);
  const integrationFilterKey = getIntegrationSetupFilterKey(productResourceDefaults);
  const integrationTypeKey = getValue(productResourceDefaults, "integrationTypeKey", "IntegrationTypeKey") || "";
  const integrationTypeLabel = integrationTypeKey || (integrationTypeValue !== null && integrationTypeValue !== undefined ? integrationTypeValue : "the required integration type");
  const providerName = getValue(productResourceDefaults, "providerName", "ProviderName") || "Provider";
  const persistedIntegrationId = resolvePersistedIntegrationId(wizard);
  const linkedIntegrationId = resolveSavedIntegrationId(wizard, values);
  const preferredIntegrationIdRef = useRef(linkedIntegrationId);
  const [mode, setMode] = useState(values?.IntegrationMode === INTEGRATION_MODE_NEW ? INTEGRATION_MODE_NEW : INTEGRATION_MODE_EXISTING);
  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState(linkedIntegrationId);
  const [draftIntegration, setDraftIntegration] = useState(() => buildIntegrationDraft(values, productResourceDefaults));
  const [metadata, setMetadata] = useState(null);
  const [loadingIntegrations, setLoadingIntegrations] = useState(true);
  const [checkingConnection, setCheckingConnection] = useState(false);
  const [localError, setLocalError] = useState("");
  const [connectionStatus, setConnectionStatus] = useState({
    ConnectionCheckStatus: values?.ConnectionCheckStatus || "",
    ConnectionCheckMessage: values?.ConnectionCheckMessage || "",
    ConnectionCheckedUtc: values?.ConnectionCheckedUtc || ""
  });

  useEffect(() => {
    coreStateRef.current = coreState;
  }, [coreState]);

  useEffect(() => {
    integrationMgrRef.current = integrationMgr;
  }, [integrationMgr]);

  useEffect(() => {
    setDraftIntegration((previous) => {
      const seedDraft = buildIntegrationDraft(values, productResourceDefaults);
      return keepObjectStateWhenEqual(previous, {
        ...seedDraft,
        ...previous,
        IntegrationType: resolveEffectiveIntegrationTypeValue(previous || seedDraft, integrationTypeValue)
      });
    });
  }, [
    integrationTypeValue,
    productResourceDefaults,
    values,
    values?.ConnectionString,
    values?.Description,
    values?.DisplayName,
    values?.IntegrationDescription,
    values?.IntegrationName,
    values?.Name,
    values?.SecureConnectionString
  ]);

  useEffect(() => {
    preferredIntegrationIdRef.current = linkedIntegrationId;
    if (!linkedIntegrationId) return;
    setSelectedIntegrationId((previous) => previous || linkedIntegrationId);
  }, [linkedIntegrationId]);

  useEffect(() => {
    let mounted = true;
    const activeMgr = integrationMgrRef.current;
    const loadKey = `${integrationFilterKey}:${persistedIntegrationId || "none"}`;

    if (!activeMgr) {
      setLoadingIntegrations(false);
      setLocalError("Integration service is not available.");
      return undefined;
    }
    if (loadKeyRef.current === loadKey) return undefined;

    loadKeyRef.current = loadKey;
    setLoadingIntegrations(true);
    setLocalError("");

    Promise.all([
      activeMgr.Query(buildIntegrationQuery(integrationTypeValue)),
      activeMgr.GetMetadata({})
    ])
      .then(([queryResult, metadataResult]) => {
        if (!mounted) return;
        requireSuccessfulServiceResponse(queryResult, "Integration records could not be loaded.", coreStateRef.current);
        requireServiceResponse(metadataResult, "Integration metadata could not be loaded.");

        const matchingIntegrations = filterIntegrationsForSetup(getOperationItems(queryResult), integrationTypeValue, productResourceDefaults);
        const metadataItem = getFirst(metadataResult?.Items)
          || getFirst(metadataResult)
          || getFirst(activeMgr.uiMetadata)
          || null;
        const preferredIntegrationId = persistedIntegrationId || preferredIntegrationIdRef.current;
        const linkedSelection = matchingIntegrations.find((integration) => String(getId(integration)) === String(preferredIntegrationId));
        const nextSelectedIntegrationId = getId(linkedSelection) || getId(getFirst(matchingIntegrations)) || "";

        setIntegrations(matchingIntegrations);
        setMetadata(metadataItem);
        setSelectedIntegrationId(nextSelectedIntegrationId);
        if (!nextSelectedIntegrationId || matchingIntegrations.length === 0) {
          setMode(INTEGRATION_MODE_NEW);
        }
      })
      .catch((ex) => {
        if (!mounted) return;
        setIntegrations([]);
        setMode(INTEGRATION_MODE_NEW);
        setLocalError(ex?.message || "Integration records could not be loaded.");
      })
      .finally(() => {
        if (mounted) setLoadingIntegrations(false);
      });

    return () => {
      mounted = false;
      if (loadKeyRef.current === loadKey) {
        loadKeyRef.current = "";
      }
    };
  }, [integrationFilterKey, integrationMgrReady, integrationTypeValue, persistedIntegrationId, productResourceDefaults]);

  const selectedIntegration = useMemo(
    () => integrations.find((integration) => String(getId(integration)) === String(selectedIntegrationId)) || null,
    [integrations, selectedIntegrationId]
  );

  const integrationFormMetadata = useMemo(() => buildSetupFormMetadata(metadata, {
    hiddenFieldNames: INTEGRATION_SETUP_HIDDEN_FIELD_NAMES
  }), [metadata]);

  const integrationFormFields = useMemo(
    () => getMetadataFields(integrationFormMetadata),
    [integrationFormMetadata]
  );

  const existingIntegrationOptions = useMemo(
    () => integrations.map((integration) => {
      const id = getId(integration);
      return {
        Text: getDisplayName(integration) || getValue(integration, "Name", "name") || id,
        Value: id,
        FilterValue: String(getValue(integration, "IntegrationType", "integrationType") ?? "")
      };
    }),
    [integrations]
  );

  const updateSelectedIntegrationId = useCallback((nextValue) => {
    const nextId = nextValue || "";
    const nextIntegration = integrations.find((integration) => String(getId(integration)) === String(nextId)) || null;
    const nextIntegrationType = resolveEffectiveIntegrationTypeValue(nextIntegration, integrationTypeValue);
    setSelectedIntegrationId(nextId);
    onChange?.("IntegrationMode", INTEGRATION_MODE_EXISTING);
    onChange?.("IntegrationId", nextId);
    onChange?.("IntegrationName", nextIntegration ? getDisplayName(nextIntegration) : "");
    onChange?.("IntegrationType", nextIntegrationType ?? "");
    onChange?.("IntegrationTypeKey", integrationTypeKey);
  }, [integrationTypeKey, integrationTypeValue, integrations, onChange]);

  const updateIntegrationMode = useCallback((nextMode) => {
    setMode(nextMode);
    onChange?.("IntegrationMode", nextMode);
    setLocalError("");
  }, [onChange]);

  const updateConnectionStatus = useCallback((status) => {
    setConnectionStatus(status);
    onChange?.("ConnectionCheckStatus", status.ConnectionCheckStatus || "");
    onChange?.("ConnectionCheckMessage", status.ConnectionCheckMessage || "");
    onChange?.("ConnectionCheckedUtc", status.ConnectionCheckedUtc || "");
  }, [onChange]);

  const resolveIntegrationType = useCallback((integration) =>
    resolveEffectiveIntegrationTypeValue(integration, integrationTypeValue), [integrationTypeValue]);

  const runConnectionCheck = useCallback(async (operationItem, options = {}) => {
    const activeMgr = integrationMgrRef.current;
    if (!activeMgr) throw new Error("Integration service is not available.");
    if (!operationItem || (options.requiresId && !getId(operationItem))) {
      throw new Error("Select an Integration before checking the connection.");
    }
    const operationIntegrationType = resolveIntegrationType(operationItem);
    if (operationIntegrationType === null) {
      const validationMessage = "Select an Integration Type before checking the connection.";
      const status = {
        ConnectionCheckStatus: "Failed",
        ConnectionCheckMessage: validationMessage,
        ConnectionCheckedUtc: new Date().toISOString()
      };
      updateConnectionStatus(status);
      throw buildWizardValidationError(validationMessage, { suppressSummary: true });
    }
    const operationItemWithType = {
      ...operationItem,
      IntegrationType: operationIntegrationType
    };

    setCheckingConnection(true);
    setLocalError("");

    try {
      const result = await activeMgr.RunOperation({
        operationName: CONNECTION_CHECK_OPERATION,
        item: operationItemWithType,
        queryParams: buildResourceOperationQueryParams({
          wizard,
          item: operationItemWithType
        }),
        isItem: true,
        isWarning: true,
        supressAllErrors: false
      });
      requireSuccessfulServiceResponse(result, "Connection check failed.", coreStateRef.current);
      const operationResult = getOperationResult(result);
      const success = result?.Success !== false && operationResult?.Success !== false;
      if (!success) {
        throw buildServerFailureError(operationResult || result, "Connection check failed.", coreStateRef.current);
      }

      const status = {
        ConnectionCheckStatus: "Success",
        ConnectionCheckMessage: operationResult?.DisplayMessage || result?.DisplayMessage || "Connection check completed.",
        ConnectionCheckedUtc: new Date().toISOString()
      };
      updateConnectionStatus(status);
      return status;
    } catch (ex) {
      const status = buildConnectionCheckFailureStatus(ex, "Connection check failed.");
      updateConnectionStatus(status);
      throw ex?.validationErrors ? ex : new Error(status.ConnectionCheckMessage || "Connection check failed.");
    } finally {
      setCheckingConnection(false);
    }
  }, [resolveIntegrationType, updateConnectionStatus, wizard]);

  const validateSelectedIntegration = useCallback(() => {
    if (!selectedIntegrationId || !selectedIntegration) {
      const validationMessage = "Select an Integration before continuing.";
      setLocalError(validationMessage);
      throw buildWizardValidationError(validationMessage, { suppressSummary: true });
    }

    setLocalError("");
  }, [selectedIntegration, selectedIntegrationId]);

  const validateDraftIntegration = useCallback((integration, groupId = INTEGRATION_NEW_FORM_GROUP_ID) => {
    if (!integrationFormMetadata) {
      const validationMessage = "Integration form metadata is not available.";
      setLocalError(validationMessage);
      throw new Error(validationMessage);
    }

    const validationMessage = validateRequiredIntegrationFields({
      ...integration,
      IntegrationType: resolveIntegrationType(integration)
    }, integrationFormFields);
    if (validationMessage) {
      setLocalError(validationMessage);
      throw buildWizardValidationError(validationMessage, { suppressSummary: true });
    }

    const connectionStringValidationMessage = validateIntegrationConnectionString({
      ...integration,
      IntegrationType: resolveIntegrationType(integration)
    }, resolveIntegrationType(integration));
    if (connectionStringValidationMessage) {
      setLocalError(connectionStringValidationMessage);
      throw buildClientValidationError("ConnectionString", connectionStringValidationMessage, coreStateRef.current, { suppressSummary: true });
    }

    setLocalError("");
  }, [integrationFormFields, integrationFormMetadata, resolveIntegrationType]);

  const saveResolvedIntegration = useCallback(async (integration, fallbackMessage = "Integration could not be saved.") => {
    const activeMgr = integrationMgrRef.current;
    if (!activeMgr) throw new Error("Integration service is not available.");

    const integrationToSave = {
      ...integration,
      IsNew: !getId(integration),
      IntegrationType: resolveIntegrationType(integration)
    };
    const saveResult = await activeMgr.saveItem(integrationToSave);
    requireSuccessfulServiceResponse(saveResult, fallbackMessage, coreStateRef.current);

    const savedIntegration = getOperationItem(saveResult)
      || getFirst(saveResult?.Items)
      || getFirst(saveResult?.items)
      || saveResult?.Item
      || saveResult?.item
      || saveResult;
    if (!getId(savedIntegration)) throw new Error("Integration save did not return an Integration id.");

    setIntegrations((previous) => {
      const withoutSaved = previous.filter((item) => String(getId(item)) !== String(getId(savedIntegration)));
      return filterIntegrationsForSetup([savedIntegration, ...withoutSaved], integrationTypeValue, productResourceDefaults);
    });
    setSelectedIntegrationId(getId(savedIntegration));
    onChange?.("IntegrationId", getId(savedIntegration));
    onChange?.("IntegrationName", getDisplayName(savedIntegration));
    onChange?.("IntegrationType", resolveIntegrationType(savedIntegration) ?? "");
    onChange?.("IntegrationTypeKey", integrationTypeKey);
    return savedIntegration;
  }, [integrationTypeKey, integrationTypeValue, onChange, productResourceDefaults, resolveIntegrationType]);

  const prepareStepValues = useCallback(async (currentValues = values, options = {}) => {
    let resolvedIntegration;
    if (mode === INTEGRATION_MODE_EXISTING) {
      validateSelectedIntegration();
      validateDraftIntegration({
        ...selectedIntegration,
        IntegrationType: resolveIntegrationType(selectedIntegration)
      }, INTEGRATION_EXISTING_FORM_GROUP_ID);
      resolvedIntegration = await saveResolvedIntegration(selectedIntegration, "Integration could not be saved.");
    } else {
      const integrationToSave = {
        ...draftIntegration,
        IsNew: !getId(draftIntegration),
        IntegrationType: resolveIntegrationType(draftIntegration)
      };
      validateDraftIntegration(integrationToSave);
      resolvedIntegration = await saveResolvedIntegration(integrationToSave, "Integration could not be saved.");
      setDraftIntegration(resolvedIntegration);
    }

    const resolvedConnectionStatus = options.action === "continue"
      ? await runConnectionCheck(resolvedIntegration, { requiresId: true })
      : connectionStatus;

    return buildIntegrationStepValues({
      currentValues,
      mode,
      integration: resolvedIntegration,
      productResourceDefaults,
      connectionStatus: resolvedConnectionStatus
    });
  }, [
    connectionStatus,
    draftIntegration,
    mode,
    productResourceDefaults,
    resolveIntegrationType,
    runConnectionCheck,
    saveResolvedIntegration,
    selectedIntegration,
    validateDraftIntegration,
    validateSelectedIntegration,
    values
  ]);

  prepareStepValuesRef.current = prepareStepValues;
  canContinueRef.current = () => {
    if (loadingIntegrations || checkingConnection) return false;
    if (!integrationFormMetadata) return false;
    if (mode === INTEGRATION_MODE_EXISTING) return Boolean(selectedIntegrationId);
    return true;
  };
  isBusyRef.current = loadingIntegrations || checkingConnection;

  useEffect(() => {
    registerStepController?.({
      continueLabel: "Save & Continue",
      canContinue: () => canContinueRef.current?.() ?? true,
      get isBusy() {
        return Boolean(isBusyRef.current);
      },
      prepareSave: (currentValues, actionContext) =>
        prepareStepValuesRef.current?.(currentValues, { ...actionContext, action: "save" }) ?? currentValues,
      prepareContinue: (currentValues, actionContext) =>
        prepareStepValuesRef.current?.(currentValues, { ...actionContext, action: "continue" }) ?? currentValues
    });
  }, [checkingConnection, integrationFormMetadata, loadingIntegrations, mode, registerStepController, selectedIntegrationId]);

  useEffect(() => () => registerStepController?.(null), [registerStepController]);

  const handleDraftFormChange = (value, fieldName, updatedItem) => {
    setDraftIntegration((previous) => {
      const mergedIntegration = {
        ...previous,
        ...(updatedItem || {}),
        [fieldName]: value
      };
      const mergedIntegrationType = resolveIntegrationType(mergedIntegration);
      return keepObjectStateWhenEqual(previous, {
        ...mergedIntegration,
        [fieldName]: fieldName === "IntegrationType" ? mergedIntegrationType : value,
        IntegrationType: mergedIntegrationType
      });
    });
  };

  const handleSelectedIntegrationFormChange = (value, fieldName, updatedItem) => {
    setIntegrations((previous) => previous.map((integration) => {
      if (String(getId(integration)) !== String(selectedIntegrationId)) return integration;
      const mergedIntegration = {
        ...integration,
        ...(updatedItem || {}),
        [fieldName]: value
      };
      const mergedIntegrationType = resolveIntegrationType(mergedIntegration);
      return {
        ...mergedIntegration,
        [fieldName]: fieldName === "IntegrationType" ? mergedIntegrationType : value,
        IntegrationType: mergedIntegrationType
      };
    }));
  };

  const handleCheckConnection = async () => {
    const operationItem = mode === INTEGRATION_MODE_EXISTING
      ? selectedIntegration
      : { ...draftIntegration, IntegrationType: resolveIntegrationType(draftIntegration) };
    let validationComplete = false;
    try {
      if (mode === INTEGRATION_MODE_EXISTING) validateSelectedIntegration();
      if (mode === INTEGRATION_MODE_NEW) validateDraftIntegration(operationItem);
      validationComplete = true;
      await runConnectionCheck(operationItem, { requiresId: mode === INTEGRATION_MODE_EXISTING });
    } catch (ex) {
      setLocalError(validationComplete ? "" : (ex?.message || "Connection check failed."));
    }
  };

  const connectionStatusText = formatConnectionCheckStatusText({
    status: connectionStatus.ConnectionCheckStatus,
    message: connectionStatus.ConnectionCheckMessage
  });

  return (
    <div className="resource-wizard__integration">
      <div className="resource-wizard__integration-header">
        <div>
          <div className="resource-wizard__integration-title-row">
            <h3>Integration</h3>
            <div className="resource-wizard__mode-control" role="group" aria-label="Integration mode">
              <ButtonControl
                type="standard-small"
                className={`resource-wizard__mode-button${mode === INTEGRATION_MODE_EXISTING ? " active" : ""}`}
                onClick={() => updateIntegrationMode(INTEGRATION_MODE_EXISTING)}
                disabled={readOnly || loadingIntegrations || integrations.length === 0}
              >
                Existing
              </ButtonControl>
              <ButtonControl
                type="standard-small"
                className={`resource-wizard__mode-button${mode === INTEGRATION_MODE_NEW ? " active" : ""}`}
                onClick={() => updateIntegrationMode(INTEGRATION_MODE_NEW)}
                disabled={readOnly}
              >
                New
              </ButtonControl>
            </div>
          </div>
          <p>Use this {providerName} Integration for {integrationTypeLabel} connection settings, credentials, and provider-specific setup. Select a matching Integration or create a new one before configuring source systems and objects.</p>
        </div>
      </div>

      {localError ? <div className="resource-wizard__alert">{localError}</div> : null}

      {loadingIntegrations ? (
        <div className="resource-wizard__integration-loading"><Loading /></div>
      ) : mode === INTEGRATION_MODE_EXISTING ? (
        <div className="resource-wizard__integration-existing">
          <FieldControl
            disabled={readOnly || integrations.length === 0}
            fieldMetadata={{
              FieldType: SystemTypes.UIFieldType.SearchSelectList,
              FieldName: "ResourceWizardIntegrationId",
              DisplayName: "Existing Integration",
              Description: `Only active ${integrationTypeKey || "matching"} Integrations are available.`,
              IsRequired: true,
              DataJson: existingIntegrationOptions,
              PlaceHolder: "Select an Integration"
            }}
            selectData={existingIntegrationOptions}
            groupId={INTEGRATION_EXISTING_FORM_GROUP_ID}
            value={selectedIntegrationId || null}
            onChange={updateSelectedIntegrationId}
          />
          {integrations.length === 0 ? (
            <div className="resource-wizard__state">No matching Integrations are available for {integrationTypeKey || "this integration type"}.</div>
          ) : null}
          {selectedIntegration && integrationFormMetadata ? (
            <div className="resource-wizard__integration-form">
              <FormControl
                key={`integration-existing-form-${selectedIntegrationId}`}
                className="resource-wizard__integration-form-control"
                metadata={integrationFormMetadata}
                formFields={integrationFormFields}
                itemData={{ ...selectedIntegration, IntegrationType: resolveIntegrationType(selectedIntegration) }}
                disableFooter={true}
                disableMinWidth={true}
                disabled={readOnly}
                groupId={INTEGRATION_EXISTING_FORM_GROUP_ID}
                tabLayout="top"
                onChange={handleSelectedIntegrationFormChange}
                onSave={() => {}}
              />
            </div>
          ) : null}
        </div>
      ) : (
        <div className="resource-wizard__integration-form">
          {integrationFormMetadata ? (
            <FormControl
              className="resource-wizard__integration-form-control"
              metadata={integrationFormMetadata}
              formFields={integrationFormFields}
              itemData={{ ...draftIntegration, IntegrationType: resolveIntegrationType(draftIntegration) }}
              disableFooter={true}
              disableMinWidth={true}
              disabled={readOnly}
              groupId={INTEGRATION_NEW_FORM_GROUP_ID}
              tabLayout="top"
              onChange={handleDraftFormChange}
              onSave={() => {}}
            />
          ) : (
            <div className="resource-wizard__state">Integration metadata is not available.</div>
          )}
        </div>
      )}

      <div className="resource-wizard__integration-actions">
        <div className={`resource-wizard__connection-status ${String(connectionStatus.ConnectionCheckStatus || "").toLowerCase()}`}>
          {connectionStatusText}
        </div>
        <div className="resource-wizard__integration-actions-right">
          <ButtonControl
            type="standard-small"
            className="resource-wizard__button resource-wizard__button--secondary"
            onClick={handleCheckConnection}
            disabled={checkingConnection || saving || readOnly || loadingIntegrations}
            loading={checkingConnection}
          >
            Check Connection
          </ButtonControl>
        </div>
      </div>
    </div>
  );
};

export default IntegrationStepV2;
